import { type ComponentProps } from 'react';

import {
	RadioGroup as ArkRadioGroup,
	type RadioGroupRootProps as ArkRadioGroupRootProps
} from '@ark-ui/react';

import type { ActionColor, Scale3 } from '../../lib/types';
import { cn } from '../../lib/utils';

type RadioGroupProps = Omit<ArkRadioGroupRootProps, 'color'> & {
	size?: Scale3;
	variant?: 'classic' | 'surface' | 'soft';
	color?: ActionColor;
	highContrast?: boolean;
};

function RadioGroupRoot({
	className,
	size = '2',
	variant = 'surface',
	color = 'accent',
	highContrast,
	...props
}: RadioGroupProps) {
	return (
		<ArkRadioGroup.Root
			className={cn('pt-radio-group', className)}
			data-size={size}
			data-variant={variant}
			data-accent-color={color}
			data-high-contrast={highContrast ? '' : undefined}
			{...props}
		/>
	);
}

function RadioGroupLabel({ className, ...props }: ComponentProps<typeof ArkRadioGroup.Label>) {
	return <ArkRadioGroup.Label className={cn('pt-radio-group-label', className)} {...props} />;
}

function RadioGroupItem({ className, ...props }: ComponentProps<typeof ArkRadioGroup.Item>) {
	return <ArkRadioGroup.Item className={cn('pt-radio-group-item', className)} {...props} />;
}

function RadioGroupItemControl({
	className,
	...props
}: ComponentProps<typeof ArkRadioGroup.ItemControl>) {
	return (
		<ArkRadioGroup.ItemControl
			className={cn('pt-radio-group-item-control', className)}
			{...props}
		/>
	);
}

function RadioGroupItemText({
	className,
	...props
}: ComponentProps<typeof ArkRadioGroup.ItemText>) {
	return (
		<ArkRadioGroup.ItemText className={cn('pt-radio-group-item-text', className)} {...props} />
	);
}

function RadioGroupIndicator({
	className,
	...props
}: ComponentProps<typeof ArkRadioGroup.Indicator>) {
	return (
		<ArkRadioGroup.Indicator className={cn('pt-radio-group-indicator', className)} {...props} />
	);
}

const RadioGroup = Object.assign(RadioGroupRoot, {
	Root: RadioGroupRoot,
	Label: RadioGroupLabel,
	Item: RadioGroupItem,
	ItemControl: RadioGroupItemControl,
	ItemText: RadioGroupItemText,
	Indicator: RadioGroupIndicator,
	ItemHiddenInput: ArkRadioGroup.ItemHiddenInput
});

export { RadioGroup, type RadioGroupProps };
